import type { AuthClient } from "./index";
import {
  HEADER_AUTHORIZATION,
  HEADER_COMPANY_ID,
  HEADER_CORRELATION_ID,
  HEADER_EMPLOYEE_ID,
  HEADER_ORGANIZATION_ID,
  HEADER_TENANT_ID,
} from "./headerNames";
import { normalizeGuidHeaderValue, normalizeHeaderValue } from "./normalize";

export type AuthHeaderDefaults = {
  tenantId?: string;
  organizationId?: string;
  companyId?: string;
  employeeId?: string;
  correlationId?: string;
};

function normalizeHeaders(additional?: HeadersInit): Record<string, string> {
  if (!additional) {
    return {};
  }

  if (typeof Headers !== "undefined" && additional instanceof Headers) {
    const headers: Record<string, string> = {};
    additional.forEach((value, key) => {
      headers[key] = value;
    });
    return headers;
  }

  if (Array.isArray(additional)) {
    return Object.fromEntries(additional);
  }

  return { ...(additional as Record<string, string>) };
}

export async function buildAuthHeadersForClient(
  client: AuthClient | undefined,
  defaults: AuthHeaderDefaults,
  additional?: HeadersInit,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  const defaultTenantId = normalizeHeaderValue(defaults.tenantId);
  if (defaultTenantId) {
    headers[HEADER_TENANT_ID] = defaultTenantId;
  }
  const defaultOrganizationId = normalizeHeaderValue(defaults.organizationId);
  if (defaultOrganizationId) {
    headers[HEADER_ORGANIZATION_ID] = defaultOrganizationId;
  }
  const defaultCompanyId = normalizeHeaderValue(defaults.companyId);
  if (defaultCompanyId) {
    headers[HEADER_COMPANY_ID] = defaultCompanyId;
  }
  const defaultEmployeeId = normalizeGuidHeaderValue(defaults.employeeId);
  if (defaultEmployeeId) {
    headers[HEADER_EMPLOYEE_ID] = defaultEmployeeId;
  }
  const defaultCorrelationId = normalizeHeaderValue(defaults.correlationId);
  if (defaultCorrelationId) {
    headers[HEADER_CORRELATION_ID] = defaultCorrelationId;
  }

  if (!client) {
    Object.assign(headers, normalizeHeaders(additional));
    return headers;
  }

  const state = client.getState();
  const tenantId =
    normalizeHeaderValue(state.tenantId) ??
    normalizeHeaderValue(defaults.tenantId);
  const organizationId = normalizeHeaderValue(state.user?.organizationId ?? defaults.organizationId);

  await client.ensureValidAccessToken({ tenantId, organizationId });

  Object.assign(headers, client.buildTenantHeaders(tenantId, organizationId));
  Object.assign(headers, normalizeHeaders(additional));

  const authorization = client.getAuthorizationHeader();
  if (authorization && !headers[HEADER_AUTHORIZATION]) {
    headers[HEADER_AUTHORIZATION] = authorization;
  }

  return headers;
}
